import { useState } from "react";

import clsx from "clsx";
import {
    Briefcase,
    ChevronDown,
    Flame,
    HandHelping,
    Heart,
    HelpCircle,
    Loader2,
    LoaderPinwheel,
    Shield,
    User,
} from "lucide-react";
import { toast } from "sonner";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useInternalFeatures } from "~/hooks/use-internal-features";
import { usePartMutation } from "~/hooks/use-session";
import { logger } from "~/lib/logger";
import { capitalize } from "~/lib/utils";

import { PART_TYPES } from "../utils/part-types";

interface PartTypeSelectorProps {
    partId: string;
    sessionId: string;
    sessionOwnerId: string;
    sessionToken: string | null;
    currentPartType?: string;
    onUpdate?: () => void;
    /** If true, the selector is read-only (for public session access) */
    isPublicSession?: boolean;
}

export function PartTypeSelector({
    partId,
    sessionId,
    sessionOwnerId,
    sessionToken,
    currentPartType,
    onUpdate,
    isPublicSession = false,
}: PartTypeSelectorProps) {
    const [isUpdating, setIsUpdating] = useState(false);
    const { enabled: internalFeaturesEnabled } = useInternalFeatures();

    const { mutateAsync: mutatePart } = usePartMutation(
        sessionId,
        sessionOwnerId,
        sessionToken,
    );

    // Clone PART_TYPES and add unattached-burden-unconfirmed to "Other" if internalFeaturesEnabled is false
    const PART_TYPE_OPTIONS = PART_TYPES.map((option) => {
        if (option.label === "Other" && !internalFeaturesEnabled) {
            return {
                ...option,
                value: option.value.concat([
                    "unattached-burden-unconfirmed",
                    "Unattached Burden (Unconfirmed)",
                ]),
            };
        }
        return option;
    }).concat(
        internalFeaturesEnabled
            ? [
                  {
                      label: "Unattached Burden (Unconfirmed, Internal)",
                      icon: Shield,
                      value: ["Unattached Burden (Unconfirmed)"],
                      extendedOptions: true,
                  },
              ]
            : [],
    );

    // Find the current part type option based on currentPartType
    const currentPartTypeOption =
        PART_TYPE_OPTIONS.find(
            (option) =>
                currentPartType &&
                option.value.some(
                    (value) =>
                        value.toLowerCase() === currentPartType.toLowerCase(),
                ),
        ) || PART_TYPE_OPTIONS[8]; // Default to "Unclear"

    const updatePartType = async (newType: string) => {
        setIsUpdating(true);
        try {
            // Use the first value in the array as the actual type
            const typeToSave = newType.split(",")[0];
            await mutatePart({
                action: "update-attribute",
                partId: partId,
                name: "type",
                content: typeToSave,
            });

            // Call the onUpdate callback if provided
            if (onUpdate) {
                onUpdate();
            }

            return Promise.resolve();
        } catch (error) {
            logger.withError(error as Error).error("Error updating part type");
            toast.error("Failed to update part type");
            return Promise.reject(error);
        } finally {
            setIsUpdating(false);
        }
    };

    // AIDEV-NOTE: For public sessions, render read-only display without dropdown
    if (isPublicSession) {
        return (
            <div className="flex items-center space-x-2 text-lg">
                {currentPartTypeOption && (
                    <currentPartTypeOption.icon className="size-5" />
                )}
                <span>
                    {currentPartTypeOption
                        ? currentPartTypeOption.label
                        : capitalize(currentPartType || "Unknown")}
                </span>
            </div>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isUpdating}>
                {/* AIDEV-NOTE: No aria-label — the visible type label IS the accessible
                    name. A static label would replace it, so a screen reader would
                    announce the action but never the currently selected type. Radix
                    already supplies aria-haspopup/aria-expanded, which conveys that
                    this opens a menu. */}
                <button
                    type="button"
                    className="group -mx-2 flex items-center gap-2 rounded-lg px-2 py-1 text-lg transition-colors hover:bg-brand-cream-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-300"
                >
                    {isUpdating ? (
                        <Loader2 className="size-5 animate-spin" />
                    ) : (
                        currentPartTypeOption && (
                            <currentPartTypeOption.icon className="size-5" />
                        )
                    )}
                    <span className={clsx(isUpdating && "opacity-50")}>
                        {currentPartTypeOption
                            ? currentPartTypeOption.label
                            : capitalize(currentPartType || "Unknown")}
                    </span>
                    {/* AIDEV-NOTE: The type has always been editable, but rendered as
                        plain text with no affordance, so nobody discovered it. */}
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="-ml-3">
                {PART_TYPE_OPTIONS.map((option) => (
                    <DropdownMenuItem
                        key={option.value.join(",")}
                        className={clsx(
                            "flex items-center cursor-pointer text-lg",
                            option.value.includes(currentPartType || "") &&
                                "font-medium",
                        )}
                        onClick={() => updatePartType(option.value[0])}
                        disabled={isUpdating}
                    >
                        <option.icon className="mr-2 size-5 text-lg" />
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
